import React, { useState, useEffect, useCallback } from 'react';
import { GraduationCap, Search, Plus, MapPin, ChevronLeft, ChevronRight, User, X, Archive } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Tabs from '../../components/common/Tabs';
import { academyAPI, sportTypeAPI } from '../../services/api';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

const AcademyListPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  // Opens on the academies you belong to. Somebody arriving here almost always
  // wants one of their own; discovery is the occasional errand, not the default.
  const [activeTab, setActiveTab] = useState('my-academies');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSportTypeId, setSelectedSportTypeId] = useState('');
  const [academies, setAcademies] = useState([]);
  const [sportTypes, setSportTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [geoCoords, setGeoCoords] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [nearMeActive, setNearMeActive] = useState(false);
  // Off by default, and only ever offered on My Academies: an archived academy
  // is one its owner has put away, so it stays out of sight until asked for.
  const [showArchived, setShowArchived] = useState(false);
  const NEAR_ME_RADIUS_KM = 25;

  useEffect(() => {
    sportTypeAPI.getAll()
      .then(res => setSportTypes(res?.data || res || []))
      .catch(() => {});
  }, []);

  const fetchAcademies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { tab: activeTab, page, limit };
      if (activeTab === 'my-academies' && showArchived) params.includeArchived = true;
      if (searchQuery)        params.search      = searchQuery;
      if (selectedSportTypeId) params.sportTypeId = selectedSportTypeId;
      if (nearMeActive && geoCoords) {
        params.lat      = geoCoords.lat;
        params.lng      = geoCoords.lng;
        params.radiusKm = NEAR_ME_RADIUS_KM;
      }
      const res = await academyAPI.getAll(params);
      setAcademies(res?.academies || []);
      setTotal(res?.total ?? 0);
    } catch (err) {
      setError('Failed to load academies');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery, selectedSportTypeId, page, limit, nearMeActive, geoCoords, showArchived]);

  useEffect(() => {
    const t = setTimeout(fetchAcademies, searchQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchAcademies, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [activeTab, searchQuery, selectedSportTypeId, nearMeActive, showArchived]);

  const handleNearMe = () => {
    if (nearMeActive) {
      setNearMeActive(false);
      setGeoCoords(null);
      setGeoError(null);
      return;
    }
    if (geoCoords) {
      setNearMeActive(true);
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearMeActive(true);
        setGeoLoading(false);
      },
      () => {
        setGeoError('Location access denied');
        setGeoLoading(false);
      },
    );
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className={`${theme.background} min-h-screen`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h1 className={`text-3xl font-bold ${theme.text} flex items-center`}>
              <GraduationCap className="mr-3" size={32} />
              Academies
            </h1>
            <button
              onClick={() => navigate('/academies/new')}
              className={`flex items-center gap-x-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg transition-colors`}
            >
              <Plus size={20} />
              <span>Create Academy</span>
            </button>
          </div>
          <p className={theme.textSecondary}>
            Join sports academies, enroll in programs, and improve your skills with professional coaching.
          </p>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={[
            { id: 'discover',     label: 'Discover Academies', shortLabel: 'Discover' },
            { id: 'my-academies', label: 'My Academies',       shortLabel: 'My Academies' },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          className="mb-6"
        />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search academies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 border ${theme.border} rounded-xl focus:ring-2 ${theme.input}`}
            />
          </div>
          <select
            value={selectedSportTypeId}
            onChange={(e) => setSelectedSportTypeId(e.target.value)}
            className={`px-4 py-3 border ${theme.border} rounded-xl focus:ring-2 ${theme.input}`}
          >
            <option value="">All Sports</option>
            {sportTypes.map(sport => (
              <option key={sport.id} value={sport.id}>{sport.sportName}</option>
            ))}
          </select>
          {/* Distance is a discovery tool. On My Academies it filtered away the
              academies you own, so it isn't offered there at all. */}
          {activeTab === 'my-academies' && (
            <button
              onClick={() => setShowArchived(v => !v)}
              aria-pressed={showArchived}
              className={`flex items-center gap-2 px-4 py-3 border text-sm font-medium rounded-xl transition ${
                showArchived
                  ? 'bg-blue-500 text-white border-blue-500'
                  : `${theme.border} ${theme.text} ${theme.cardHover}`
              }`}
            >
              <Archive size={16} />
              {showArchived ? 'Showing archived' : 'Show archived'}
            </button>
          )}
          {activeTab !== 'my-academies' && (
            <div className="flex items-stretch">
              <button
                onClick={handleNearMe}
                disabled={geoLoading}
                className={`flex items-center gap-2 px-4 py-3 border text-sm font-medium transition ${
                  nearMeActive
                    ? 'bg-blue-500 text-white border-blue-500 rounded-l-xl'
                    : `${theme.border} ${theme.text} ${theme.cardHover} rounded-xl`
                }`}
              >
                <MapPin size={16} />
                {geoLoading ? 'Locating…' : nearMeActive ? `Within ${NEAR_ME_RADIUS_KM} km` : 'Near me'}
              </button>
              {/* Tapping the chip already toggled the filter off, but nothing
                  said so — it read as a static label with no way back. */}
              {nearMeActive && !geoLoading && (
                <button
                  onClick={handleNearMe}
                  title="Clear location filter"
                  aria-label="Clear location filter"
                  className="flex items-center px-2.5 border border-l-0 border-blue-500 bg-blue-500 text-white rounded-r-xl hover:bg-blue-600 transition"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        </div>
        {geoError && <p className="text-red-500 text-sm mb-4">{geoError}</p>}

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : error ? (
          <div className={`${theme.card} text-center p-12 rounded-xl`}>
            <p className="text-red-500">{error}</p>
          </div>
        ) : academies.length === 0 ? (
          <div className={`${theme.card} text-center p-12 rounded-xl`}>
            <GraduationCap size={48} className="mx-auto mb-3 opacity-50" />
            <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No academies found</h5>
            <p className={theme.textSecondary}>
              {activeTab !== 'my-academies'
                ? 'Try adjusting your search filters.'
                : showArchived
                  ? 'You have not created any academy yet.'
                  : 'You have not created any academy yet — or they are all archived. Try "Show archived".'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {academies.map(academy => (
                <AcademyCard key={academy.id} academy={academy} theme={theme} navigate={navigate} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className={`p-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-40 ${theme.cardHover} transition`}
                >
                  <ChevronLeft size={18} />
                </button>
                <span className={`text-sm ${theme.textSecondary}`}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className={`p-2 rounded-lg border ${theme.border} ${theme.text} disabled:opacity-40 ${theme.cardHover} transition`}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

function AcademyCard({ academy, theme, navigate }) {
  const [imgIndex, setImgIndex] = useState(0);

  const roleLabel = academy.userRole
    ? academy.userRole.charAt(0).toUpperCase() + academy.userRole.slice(1)
    : null;

  const isOwnerOrAdmin = academy.userRole === 'owner' || academy.userRole === 'admin';

  const sportNames = academy.sportTypes?.map(s => s.sportName).join(', ') || '';
  const detailUrl   = `/academy/${academy.slug}`;
  const manageUrl   = `/academy/${academy.slug}?tab=settings`;

  const images = (academy.images || []).map(resolveImageUrl);
  const hasCarousel = images.length > 1;
  const heroSrc = images.length > 0 ? images[imgIndex] : resolveImageUrl(academy.backgroundImage);

  const prevImg = (e) => { e.stopPropagation(); setImgIndex(i => (i - 1 + images.length) % images.length); };
  const nextImg = (e) => { e.stopPropagation(); setImgIndex(i => (i + 1) % images.length); };

  return (
    <div className={`${theme.card} rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg`}>
      {/* Hero / carousel */}
      <div
        onClick={() => navigate(detailUrl)}
        style={{
          height: '180px',
          backgroundImage: heroSrc ? `url(${heroSrc})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        className={`relative cursor-pointer ${heroSrc ? '' : 'bg-gray-700'}`}
      >
        {roleLabel && (
          <span className={`absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-semibold ${
            isOwnerOrAdmin ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
          }`}>
            {roleLabel}
          </span>
        )}
        {/* Only ever reached with "Show archived" on, but the owner still needs
            to tell which of the cards in front of them are the put-away ones. */}
        {academy.archivedAt && (
          <span className="absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-semibold bg-gray-900/80 text-white flex items-center gap-1">
            <Archive size={12} /> Archived
          </span>
        )}
        {!heroSrc && (
          <div className="flex items-center justify-center h-full">
            <GraduationCap size={48} className="text-gray-500" />
          </div>
        )}
        {hasCarousel && (
          <>
            <button onClick={prevImg} className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1 hover:bg-black/60">
              <ChevronLeft size={14} />
            </button>
            <button onClick={nextImg} className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/40 text-white rounded-full p-1 hover:bg-black/60">
              <ChevronRight size={14} />
            </button>
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
              {images.map((_, i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === imgIndex ? 'bg-white' : 'bg-white/50'}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="p-4">
        <div className="cursor-pointer" onClick={() => navigate(detailUrl)}>
          <div className="flex justify-between items-start mb-2">
            <h5 className={`text-lg font-semibold ${theme.text}`}>{academy.name}</h5>
            {sportNames && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${theme.tag} ml-2 shrink-0`}>
                {academy.sportTypes.length > 1 ? `${academy.sportTypes.length} sports` : sportNames}
              </span>
            )}
          </div>

          <p className={`${theme.textSecondary} text-sm mb-2 line-clamp-2`}>
            {academy.description ? academy.description.replace(/<[^>]*>/g, '') : 'No description'}
          </p>

          {academy.ownerName && (
            <div className={`flex items-center gap-1 text-xs ${theme.textSecondary} mb-3`}>
              <User size={12} />
              <span>{academy.ownerName}</span>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => navigate(detailUrl)}
            className={`flex-1 py-2 border ${theme.border} rounded-lg text-sm ${theme.text} ${theme.cardHover} transition`}
          >
            View
          </button>
          {/* No Join action. Membership grants nothing a signed-in visitor
              cannot already do — the one real difference, self-enrolling in a
              class, is granted automatically by registering for a programme
              (see registerForProgram: "registering them is what makes them a
              member"). A button whose only effect is a row in a table is worse
              than no button. */}
          {academy.userRole && isOwnerOrAdmin ? (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(manageUrl); }}
              className={`flex-1 py-2 ${theme.buttonPrimary} rounded-lg text-sm`}
            >
              Manage
            </button>
          ) : null}
        </div>

        {/* Public view link (only when published) */}
        {academy.publishedAt && academy.publicUrl && (
          <a
            href={academy.publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center text-xs text-blue-500 hover:underline mt-1"
            onClick={(e) => e.stopPropagation()}
          >
            See public view
          </a>
        )}
      </div>
    </div>
  );
}

export default AcademyListPage;
